/**
 * Registry of review items for each medical history section.
 * Each section defines how to extract per-question label/value pairs
 * from patient data, and which screen to navigate to for editing.
 *
 * All user-facing strings are routed through the i18n translation function `t`
 * provided by callers (e.g. SectionReviewScreen) via `useTranslations()`.
 */

import type { TranslationFunction } from "@src/packages/i18n";
import { formatDate as formatLocalizedDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import {
  MEDICATION_LABEL_TO_ID,
  getMedicationProductLabelKey,
} from "@src/packages/medical-history/medical/medications-vaccines/options";
import { deriveCycleLengthRange } from "@src/packages/medical-history/gynae/menstrual-health/deriveCycleLengthRange";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Patient = any;

function localizeMedicationName(
  name: string | undefined,
  t: TranslationFunction,
): string | undefined {
  if (!name) return name;
  const key = name.toLowerCase().trim();
  const categoryId = MEDICATION_LABEL_TO_ID[key];
  if (categoryId) {
    return t(getMedicationProductLabelKey(categoryId));
  }
  return name;
}

export type ReviewItem = {
  label: string;
  value: string | null;
  /** Route name of the flow, e.g. "BasicInfoFlow" */
  flowRoute: string;
  /** Screen within the flow to navigate to for editing */
  screenName?: string;
};

export type SectionReviewConfig = {
  /** i18n key for the section title — resolve via `t(titleKey)` */
  titleKey: string;
  flowRoute: string;
  getItems: (
    patient: Patient,
    t: TranslationFunction,
    locale: Locale,
  ) => ReviewItem[];
};

function yn(
  t: TranslationFunction,
  v: boolean | null | undefined,
): string | null {
  if (v === true) return t("med_history.shared.yes");
  if (v === false) return t("med_history.shared.no");
  return null;
}

function formatDate(
  d: string | null | undefined,
  locale: Locale,
): string | null {
  if (!d) return null;
  try {
    return formatLocalizedDate(d, locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function humanize(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const SECTION_REVIEW_CONFIGS: Record<string, SectionReviewConfig> = {
  "basic-info": {
    titleKey: "med_history.review.section_title.basic-info",
    flowRoute: "BasicInfoFlow",
    getItems: (patient, t, locale) => {
      const mh = patient.medicalHistory;
      const ob = patient.obGynHistory;
      const bd = patient.bodyData;
      const items: ReviewItem[] = [
        {
          label: t("med_history.review.item.sex_at_birth"),
          value:
            patient.gender === "AFAB"
              ? t("med_history.review.item.female")
              : patient.gender === "AMAB"
                ? t("med_history.review.item.male")
                : null,
          flowRoute: "BasicInfoFlow",
          screenName: "SexAtBirth",
        },
        {
          label: t("med_history.review.item.date_of_birth"),
          value: formatDate(patient.dateOfBirth, locale),
          flowRoute: "BasicInfoFlow",
          screenName: "BasicInfo",
        },
        {
          label: t("med_history.review.item.height"),
          value: bd?.height ? `${bd.height} cm` : null,
          flowRoute: "BasicInfoFlow",
          screenName: "BasicInfo",
        },
        {
          label: t("med_history.review.item.weight"),
          value: bd?.weight ? `${bd.weight} kg` : null,
          flowRoute: "BasicInfoFlow",
          screenName: "BasicInfo",
        },
      ];

      if (patient.gender === "AFAB") {
        items.push(
          {
            label: t("med_history.review.item.been_pregnant_before"),
            value: yn(t, ob?.hasBeenPregnant),
            flowRoute: "BasicInfoFlow",
            screenName: "Pregnancy",
          },
          {
            label: t("med_history.review.item.endometriosis"),
            value: mh?.hasEndometriosis ? humanize(mh.hasEndometriosis) : null,
            flowRoute: "BasicInfoFlow",
            screenName: "Endometriosis",
          },
          {
            label: t("med_history.review.item.pcos"),
            value:
              mh?.pcosDiagnosed === true
                ? t("med_history.review.item.pcos_diagnosed")
                : mh?.suspectPCOS === true
                  ? t("med_history.review.item.pcos_suspected")
                  : mh?.suspectPCOS === false
                    ? t("med_history.shared.no")
                    : null,
            flowRoute: "BasicInfoFlow",
            screenName: "PCOS",
          },
        );
      }

      if (patient.gender === "AMAB") {
        items.push({
          label: t("med_history.review.item.varicocele"),
          value: mh?.hadVaricoceles ? humanize(mh.hadVaricoceles) : null,
          flowRoute: "BasicInfoFlow",
          screenName: "Varicocele",
        });
      }

      items.push(
        {
          label: t("med_history.review.item.cancer_history"),
          value: yn(t, mh?.hasCancerHistory),
          flowRoute: "BasicInfoFlow",
          screenName: "Cancer",
        },
        {
          label: t("med_history.review.item.allergies"),
          value:
            mh?.hasAllergies === false
              ? t("med_history.review.item.no_known_allergies")
              : Array.isArray(mh?.allergies) && mh.allergies.length > 0
                ? mh.allergies
                    .map(
                      (a: { name?: string }) =>
                        a.name ||
                        t("med_history.review.item.unknown_allergy_name"),
                    )
                    .join(", ")
                : yn(t, mh?.hasAllergies),
          flowRoute: "BasicInfoFlow",
          screenName: "Allergies",
        },
        {
          label: t("med_history.review.item.currently_taking_medications"),
          value:
            mh?.currentlyTakingMedications === false
              ? t("med_history.shared.no")
              : Array.isArray(mh?.medications) && mh.medications.length > 0
                ? mh.medications
                    .map(
                      (m: { name?: string }) =>
                        localizeMedicationName(m.name, t) ||
                        t("med_history.review.item.unknown_allergy_name"),
                    )
                    .join(", ")
                : yn(t, mh?.currentlyTakingMedications),
          flowRoute: "BasicInfoFlow",
          screenName: "Medications",
        },
      );

      return items;
    },
  },

  infertility: {
    titleKey: "med_history.review.section_title.infertility",
    flowRoute: "InfertilityFlow",
    getItems: (patient, t) => {
      const ih = patient.fertilityHistory?.infertilityHistory;
      const items: ReviewItem[] = [
        {
          label: t("med_history.review.item.infertility_diagnosis"),
          value:
            ih?.isDiagnosedWithInfertility === true
              ? t("med_history.review.item.infertility_yes_diagnosed")
              : ih?.isARTNeededForCarrierDisease === true
                ? t("med_history.review.item.infertility_art_carrier")
                : ih?.isDiagnosedWithInfertility === false
                  ? t("med_history.shared.no")
                  : null,
          flowRoute: "InfertilityFlow",
          screenName: "InfertilityIntro",
        },
      ];

      if (ih?.isDiagnosedWithInfertility || ih?.isARTNeededForCarrierDisease) {
        const causes = ih?.causes;
        items.push({
          label: t("med_history.review.item.causes"),
          value:
            Array.isArray(causes) && causes.length > 0
              ? causes
                  .map((c: { cause?: string }) => humanize(c.cause))
                  .join(", ")
              : null,
          flowRoute: "InfertilityFlow",
          screenName: "InfertilityCause",
        });
      }

      items.push({
        label: t("med_history.review.item.partner_infertility"),
        value: yn(t, ih?.isPartnerInfertile),
        flowRoute: "InfertilityFlow",
        screenName: "PartnerInfertilityDiagnosis",
      });

      if (ih?.isPartnerInfertile) {
        items.push({
          label: t("med_history.review.item.partner_details"),
          value:
            [ih?.partnerInfertilityCause, ih?.notes]
              .filter((value): value is string => !!value?.trim())
              .join("\n\n") || null,
          flowRoute: "InfertilityFlow",
          screenName: "PartnerInfertilityDiagnosis",
        });
      }

      return items;
    },
  },

  "fertility-testing": {
    titleKey: "med_history.review.section_title.fertility-testing",
    flowRoute: "FertilityTestingFlow",
    getItems: (patient, t, locale) => {
      const results =
        patient.fertilityHistory?.testResults ?? patient.testResults;
      if (!Array.isArray(results) || results.length === 0) {
        return [
          {
            label: t("med_history.review.item.test_results"),
            value: t("med_history.review.item.no_test_results"),
            flowRoute: "FertilityTestingFlow",
          },
        ];
      }
      return results.map(
        (
          r: { testName?: string; result?: string; date?: string },
          i: number,
        ) => ({
          label: r.testName
            ? humanize(r.testName)
            : t("med_history.review.item.test_n", {
                params: { n: String(i + 1) },
              }),
          value:
            [r.result, formatDate(r.date, locale)]
              .filter(Boolean)
              .join(" · ") || null,
          flowRoute: "FertilityTestingFlow",
        }),
      );
    },
  },

  // Fertility-treatments never renders through `SectionReviewScreen`: the
  // resolver redirects a completed section straight to `AllPastCyclesScreen`
  // (the canonical past-cycles surface). The entry is kept as a defensive
  // fallback for anyone who deep-links `SectionReview` with this key, and
  // reads `patient.cycles` directly — the `fertilityHistory.treatments`
  // fallback that used to sit here referred to a field the OpenAPI schema
  // never modelled.
  "fertility-treatments": {
    titleKey: "med_history.review.section_title.fertility-treatments",
    flowRoute: "FertilityTreatmentsFlow",
    getItems: (patient, t) => {
      const cycles = patient.cycles;
      if (!Array.isArray(cycles) || cycles.length === 0) {
        return [
          {
            label: t("med_history.review.item.treatments"),
            value: t("med_history.review.item.no_treatments"),
            flowRoute: "FertilityTreatmentsFlow",
          },
        ];
      }
      return cycles.map(
        (
          c: { type?: string; cycleType?: string; outcome?: string },
          i: number,
        ) => ({
          label: t("med_history.review.item.treatment_n", {
            params: {
              name: humanize(
                c.type || c.cycleType || t("med_history.review.item.treatment"),
              ),
              n: String(i + 1),
            },
          }),
          value: c.outcome
            ? humanize(c.outcome)
            : t("med_history.shared.recorded"),
          flowRoute: "FertilityTreatmentsFlow",
        }),
      );
    },
  },

  "menstrual-health": {
    titleKey: "med_history.review.section_title.menstrual-health",
    flowRoute: "MenstrualHealthFlow",
    getItems: (patient, t) => {
      const mh = patient.obGynHistory?.menstrualHealth;
      const inferredCycleLength = deriveCycleLengthRange([
        mh?.period1StartDate,
        mh?.period2StartDate,
        mh?.period3StartDate,
      ]);
      const minCycleLength =
        inferredCycleLength?.minAverageCycleLength ?? mh?.minAverageCycleLength;
      const maxCycleLength =
        inferredCycleLength?.maxAverageCycleLength ?? mh?.maxAverageCycleLength;
      const items: ReviewItem[] = [
        {
          label: t("med_history.review.item.cycle_status"),
          value: mh?.cycleStatus ? humanize(mh.cycleStatus) : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "CycleStatus",
        },
        {
          label: t("med_history.review.item.cycle_length"),
          value:
            minCycleLength != null && maxCycleLength != null
              ? t("med_history.review.item.cycle_length_days", {
                  params: {
                    count:
                      minCycleLength === maxCycleLength
                        ? String(minCycleLength)
                        : `${minCycleLength}-${maxCycleLength}`,
                  },
                })
              : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "Last3Periods",
        },
        {
          label: t("med_history.review.item.flow_intensity"),
          value: mh?.menstrualFlowIntensity
            ? humanize(mh.menstrualFlowIntensity)
            : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "Symptoms",
        },
        {
          label: t("med_history.review.item.period_length"),
          value: mh?.periodLength
            ? t("med_history.review.item.period_length_days", {
                params: { count: String(mh.periodLength) },
              })
            : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "Last3Periods",
        },
        {
          label: t("med_history.review.item.pain_scale"),
          value:
            mh?.painScale != null
              ? t("med_history.review.item.pain_scale_value", {
                  params: { value: String(mh.painScale) },
                })
              : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "Symptoms",
        },
        {
          label: t("med_history.review.item.period_details"),
          value:
            Array.isArray(mh?.symptoms) && mh.symptoms.length > 0
              ? mh.symptoms
                  .map(
                    (symptom: { symptom?: string; other?: string }) =>
                      symptom.other || humanize(symptom.symptom),
                  )
                  .filter(Boolean)
                  .join(", ") || null
              : null,
          flowRoute: "MenstrualHealthFlow",
          screenName: "Symptoms",
        },
      ];

      const periods = mh?.last3Periods;
      if (Array.isArray(periods) && periods.length > 0) {
        items.push({
          label: t("med_history.review.item.last_periods_tracked"),
          value: t(
            periods.length === 1
              ? "med_history.review.item.periods_recorded_one"
              : "med_history.review.item.periods_recorded_other",
            { params: { count: String(periods.length) } },
          ),
          flowRoute: "MenstrualHealthFlow",
          screenName: "Last3Periods",
        });
      }

      return items;
    },
  },

  "sexual-health": {
    titleKey: "med_history.review.section_title.sexual-health",
    flowRoute: "SexualHealthFlow",
    getItems: (patient, t) => {
      const sh = patient.obGynHistory?.sexualHealth;
      const isMale = patient.gender === "AMAB";
      const items: ReviewItem[] = [];

      if (!isMale) {
        items.push(
          {
            label: t("med_history.review.item.had_pap_smear"),
            value: yn(t, sh?.hadPapSmear),
            flowRoute: "SexualHealthFlow",
            screenName: "PapSmear",
          },
          {
            label: t("med_history.review.item.pap_smear_result_abnormal"),
            value: yn(t, sh?.papSmearResultAbnormal),
            flowRoute: "SexualHealthFlow",
            screenName: "PapSmear",
          },
          {
            label: t("med_history.review.item.abnormal_pap_smear_procedures"),
            value: yn(t, sh?.hadPapSmearProcedures),
            flowRoute: "SexualHealthFlow",
            screenName: "PapSmearProcedures",
          },
        );
      }

      items.push({
        label: t("med_history.review.item.std_history"),
        value: yn(t, sh?.hadSTDs),
        flowRoute: "SexualHealthFlow",
        screenName: "STDs",
      });

      if (!isMale) {
        items.push({
          label: t("med_history.review.item.vaginal_infections"),
          value: yn(t, sh?.hasVaginalInfectionHistory),
          flowRoute: "SexualHealthFlow",
          screenName: "VaginalInfections",
        });
      }

      items.push(
        {
          label: t("med_history.review.item.painful_intercourse"),
          value: yn(t, sh?.hasPainfulIntercourse),
          flowRoute: "SexualHealthFlow",
          screenName: "PainfulIntercourse",
        },
        {
          label: t("med_history.review.item.uses_lubricant"),
          value: yn(t, sh?.usesLubricant),
          flowRoute: "SexualHealthFlow",
          screenName: "Lubricants",
        },
      );

      if (!isMale || sh?.isPartnerEjaculatingInVagina != null) {
        items.push({
          label: t(
            isMale
              ? "med_history.review.item.self_ejaculates_in_partner_vagina"
              : "med_history.review.item.partner_ejaculates_in_vagina",
          ),
          value: yn(t, sh?.isPartnerEjaculatingInVagina),
          flowRoute: "SexualHealthFlow",
          screenName: "PartnerEjaculate",
        });
      }

      if (isMale) {
        const maleSh = patient.maleHistory?.sexualHealth;
        items.push({
          label: t("med_history.review.item.erectile_difficulties"),
          value: yn(t, maleSh?.hasDifficultiesWithErection),
          flowRoute: "SexualHealthFlow",
          screenName: "ErectileDifficulties",
        });
      }

      return items;
    },
  },

  contraception: {
    titleKey: "med_history.review.section_title.contraception",
    flowRoute: "ContraceptionFlow",
    getItems: (patient, t) => {
      const isMale = patient.gender === "AMAB";
      const methods = isMale
        ? patient.maleHistory?.contraceptions
        : patient.obGynHistory?.contraception;

      if (!isMale && patient.obGynHistory?.hasContraceptionHistory === false) {
        return [
          {
            label: t("med_history.review.item.contraception_history"),
            value: t("med_history.review.item.no_contraception_history"),
            flowRoute: "ContraceptionFlow",
            screenName: "ContraceptionMethods",
          },
        ];
      }

      if (!Array.isArray(methods) || methods.length === 0) {
        return [
          {
            label: t("med_history.review.item.contraception_methods"),
            value: isMale
              ? null
              : yn(t, patient.obGynHistory?.hasContraceptionHistory),
            flowRoute: "ContraceptionFlow",
            screenName: "ContraceptionMethods",
          },
        ];
      }

      return methods.map(
        (m: { method?: string; contraceptionName?: string }, i: number) => ({
          label: m.method
            ? humanize(m.method)
            : t("med_history.review.item.method_n", {
                params: { n: String(i + 1) },
              }),
          value: m.contraceptionName || t("med_history.shared.recorded"),
          flowRoute: "ContraceptionFlow",
          screenName: "ContraceptionDetails",
        }),
      );
    },
  },

  pregnancies: {
    titleKey: "med_history.review.section_title.pregnancies",
    flowRoute: "PregnanciesFlow",
    getItems: (patient, t) => {
      const ob = patient.obGynHistory;
      if (ob?.hasBeenPregnant === false) {
        return [
          {
            label: t("med_history.review.item.pregnancy_history"),
            value: t("med_history.review.item.no_pregnancies"),
            flowRoute: "PregnanciesFlow",
          },
        ];
      }
      const preg = ob?.pregnancies;
      if (!Array.isArray(preg) || preg.length === 0) {
        return [
          {
            label: t("med_history.review.item.pregnancies"),
            value: yn(t, ob?.hasBeenPregnant),
            flowRoute: "PregnanciesFlow",
          },
        ];
      }
      return preg.map((p: { outcome?: string; year?: number }, i: number) => ({
        label: t("med_history.review.item.pregnancy_n", {
          params: { n: String(i + 1) },
        }),
        value:
          [p.outcome ? humanize(p.outcome) : null, p.year]
            .filter(Boolean)
            .join(" · ") || t("med_history.shared.recorded"),
        flowRoute: "PregnanciesFlow",
        screenName: "PregnancyDetails",
      }));
    },
  },

  "medications-vaccines": {
    titleKey: "med_history.review.section_title.medications-vaccines",
    flowRoute: "MedicationsVaccinesFlow",
    getItems: (patient, t, locale) => {
      const mh = patient.medicalHistory;
      const items: ReviewItem[] = [];

      items.push({
        label: t("med_history.review.item.currently_taking_medications"),
        value: yn(t, mh?.currentlyTakingMedications),
        flowRoute: "MedicationsVaccinesFlow",
        screenName: "MedicationsIntro",
      });

      const meds = mh?.medications;
      if (Array.isArray(meds) && meds.length > 0) {
        meds.forEach((m: { name?: string; frequency?: string }) => {
          items.push({
            label:
              localizeMedicationName(m.name, t) ||
              t("med_history.review.item.medication"),
            value: m.frequency
              ? humanize(m.frequency)
              : t("med_history.shared.recorded"),
            flowRoute: "MedicationsVaccinesFlow",
            screenName: "MedicationDetails",
          });
        });
      }

      const vacc = mh?.vaccinations;
      if (Array.isArray(vacc) && vacc.length > 0) {
        vacc.forEach((v: { type?: string; dateReceived?: string }) => {
          items.push({
            label: v.type
              ? humanize(v.type)
              : t("med_history.review.item.vaccine"),
            value:
              formatDate(v.dateReceived, locale) ||
              t("med_history.shared.recorded"),
            flowRoute: "MedicationsVaccinesFlow",
            screenName: "VaccineDetails",
          });
        });
      } else {
        items.push({
          label: t("med_history.review.item.vaccinations"),
          value: t("med_history.shared.none_recorded"),
          flowRoute: "MedicationsVaccinesFlow",
          screenName: "VaccinesSelect",
        });
      }

      return items;
    },
  },

  allergies: {
    titleKey: "med_history.review.section_title.allergies",
    flowRoute: "AllergiesFlow",
    getItems: (patient, t) => {
      const mh = patient.medicalHistory;
      if (mh?.hasAllergies === false) {
        return [
          {
            label: t("med_history.review.item.allergies"),
            value: t("med_history.review.item.no_known_allergies"),
            flowRoute: "AllergiesFlow",
            screenName: "AllergiesIntro",
          },
        ];
      }
      const allergies = mh?.allergies;
      if (!Array.isArray(allergies) || allergies.length === 0) {
        return [
          {
            label: t("med_history.review.item.has_allergies"),
            value: yn(t, mh?.hasAllergies),
            flowRoute: "AllergiesFlow",
            screenName: "AllergiesIntro",
          },
        ];
      }
      return allergies.map(
        (a: { allergy?: string; name?: string; comments?: string }) => ({
          label: humanize(
            a.allergy || a.name || t("med_history.review.item.allergies"),
          ),
          value: a.comments || t("med_history.shared.recorded"),
          flowRoute: "AllergiesFlow",
          screenName: "AllergiesDetails",
        }),
      );
    },
  },

  diseases: {
    titleKey: "med_history.review.section_title.diseases",
    flowRoute: "DiseaseHistoryFlow",
    getItems: (patient, t) => {
      const mh = patient.medicalHistory;
      const diseases = mh?.diseases;
      if (!Array.isArray(diseases) || diseases.length === 0) {
        return [
          {
            label: t("med_history.review.item.disease_history"),
            value: t("med_history.review.item.no_conditions_reported"),
            flowRoute: "DiseaseHistoryFlow",
          },
        ];
      }
      return diseases.map((d: { disease?: string; category?: string }) => ({
        label: humanize(d.disease || t("med_history.review.item.condition")),
        value: d.category
          ? humanize(d.category)
          : t("med_history.shared.recorded"),
        flowRoute: "DiseaseHistoryFlow",
      }));
    },
  },

  surgeries: {
    titleKey: "med_history.review.section_title.surgeries",
    flowRoute: "SurgeriesFlow",
    getItems: (patient, t, locale) => {
      const mh = patient.medicalHistory;
      if (mh?.hasSurgeries === false) {
        return [
          {
            label: t("med_history.review.item.surgical_history"),
            value: t("med_history.review.item.no_surgeries"),
            flowRoute: "SurgeriesFlow",
          },
        ];
      }
      const surg = mh?.surgeries;
      if (!Array.isArray(surg) || surg.length === 0) {
        return [
          {
            label: t("med_history.review.item.has_surgeries"),
            value: yn(t, mh?.hasSurgeries),
            flowRoute: "SurgeriesFlow",
          },
        ];
      }
      return surg.map((s: { surgery?: string; date?: string }) => ({
        label: humanize(s.surgery || t("med_history.review.item.surgery")),
        value: formatDate(s.date, locale) || t("med_history.shared.recorded"),
        flowRoute: "SurgeriesFlow",
      }));
    },
  },

  exposure: {
    titleKey: "med_history.review.section_title.exposure",
    flowRoute: "ExposureFlow",
    getItems: (patient, t) => {
      const ls = patient.lifestyle;
      if (ls?.hasExposures === false) {
        return [
          {
            label: t("med_history.review.item.environmental_exposures"),
            value: t("med_history.review.item.no_exposures_reported"),
            flowRoute: "ExposureFlow",
          },
        ];
      }
      const exp = ls?.exposures;
      if (!Array.isArray(exp) || exp.length === 0) {
        return [
          {
            label: t("med_history.review.item.has_exposures"),
            value: yn(t, ls?.hasExposures),
            flowRoute: "ExposureFlow",
          },
        ];
      }
      return exp.map((e: { exposure?: string; frequency?: string }) => ({
        label: humanize(e.exposure || t("med_history.review.item.exposure")),
        value: e.frequency
          ? humanize(e.frequency)
          : t("med_history.shared.recorded"),
        flowRoute: "ExposureFlow",
      }));
    },
  },

  substances: {
    titleKey: "med_history.review.section_title.substances",
    flowRoute: "SubstancesFlow",
    getItems: (patient, t) => {
      const ls = patient.lifestyle;
      if (ls?.hasAddictions === false) {
        return [
          {
            label: t("med_history.review.item.substance_use"),
            value: t("med_history.review.item.no_substances_reported"),
            flowRoute: "SubstancesFlow",
          },
        ];
      }
      const add = ls?.addictions;
      if (!Array.isArray(add) || add.length === 0) {
        return [
          {
            label: t("med_history.review.item.uses_substances"),
            value: yn(t, ls?.hasAddictions),
            flowRoute: "SubstancesFlow",
          },
        ];
      }
      return add.map((a: { substance?: string; frequency?: string }) => ({
        label: humanize(a.substance || t("med_history.review.item.substance")),
        value: a.frequency
          ? humanize(a.frequency)
          : t("med_history.shared.recorded"),
        flowRoute: "SubstancesFlow",
      }));
    },
  },

  "sleep-activity": {
    titleKey: "med_history.review.section_title.sleep-activity",
    flowRoute: "SleepActivityFlow",
    getItems: (patient, t) => {
      const ls = patient.lifestyle;
      const items: ReviewItem[] = [];

      items.push({
        label: t("med_history.review.item.hours_of_sleep"),
        value:
          ls?.minHoursOfSleep && ls?.maxHoursOfSleep
            ? t("med_history.review.item.hours_of_sleep_range", {
                params: {
                  min: String(ls.minHoursOfSleep),
                  max: String(ls.maxHoursOfSleep),
                },
              })
            : null,
        flowRoute: "SleepActivityFlow",
      });

      items.push({
        label: t("med_history.review.item.exercises_regularly"),
        value: yn(t, ls?.isExercising),
        flowRoute: "SleepActivityFlow",
      });

      if (ls?.exerciseType) {
        items.push({
          label: t("med_history.review.item.exercise_type"),
          value: humanize(ls.exerciseType),
          flowRoute: "SleepActivityFlow",
        });
      }

      if (ls?.exerciseFrequency) {
        items.push({
          label: t("med_history.review.item.exercise_frequency"),
          value: humanize(ls.exerciseFrequency),
          flowRoute: "SleepActivityFlow",
        });
      }

      return items;
    },
  },

  "diet-supplements": {
    titleKey: "med_history.review.section_title.diet-supplements",
    flowRoute: "DietSupplementsFlow",
    getItems: (patient, t) => {
      const ls = patient.lifestyle;
      const items: ReviewItem[] = [];

      const diets = ls?.diets;
      if (Array.isArray(diets) && diets.length > 0) {
        items.push({
          label: t("med_history.review.item.diet"),
          value: diets
            .map((d: { diet?: string }) => humanize(d.diet))
            .join(", "),
          flowRoute: "DietSupplementsFlow",
        });
      }

      const supps = ls?.dietSupplements;
      if (Array.isArray(supps) && supps.length > 0) {
        supps.forEach((s: { name?: string; supplement?: string }) => {
          items.push({
            label: humanize(
              s.name || s.supplement || t("med_history.review.item.supplement"),
            ),
            value: t("med_history.review.item.taking"),
            flowRoute: "DietSupplementsFlow",
          });
        });
      }

      if (items.length === 0) {
        items.push({
          label: t("med_history.review.item.diet_and_supplements"),
          value: t("med_history.review.item.no_diet_supplements_recorded"),
          flowRoute: "DietSupplementsFlow",
        });
      }

      return items;
    },
  },

  "family-history": {
    titleKey: "med_history.review.section_title.family-history",
    flowRoute: "FamilyHistoryFlow",
    getItems: (patient, t) => {
      const fh = patient.familyHistory;
      const items: ReviewItem[] = [];

      const ancestry = fh?.ancestryHistory;
      if (Array.isArray(ancestry) && ancestry.length > 0) {
        const labels = ancestry
          .map((a: { ancestry?: string }) =>
            a.ancestry
              ? t(`med_history.family_history.ancestry.${a.ancestry}`)
              : null,
          )
          .filter((v: string | null): v is string => Boolean(v));
        items.push({
          label: t("med_history.review.item.ancestry"),
          value: labels.length > 0 ? labels.join(", ") : null,
          flowRoute: "FamilyHistoryFlow",
          screenName: "Ethnicity",
        });
      }

      if (fh?.hasDoneDNAAncestryReport != null) {
        items.push({
          label: t("med_history.review.item.dna_test"),
          value: yn(t, fh.hasDoneDNAAncestryReport),
          flowRoute: "FamilyHistoryFlow",
          screenName: "DnaTest",
        });
      }

      if (fh?.adoptionStatus) {
        items.push({
          label: t("med_history.review.item.adoption_status"),
          value: t(`med_history.family_history.adoption.${fh.adoptionStatus}`),
          flowRoute: "FamilyHistoryFlow",
          screenName: "Adopted",
        });
      }

      if (fh?.hasRelativeFacingFertilityIssues != null) {
        items.push({
          label: t("med_history.review.item.family_infertility"),
          value: yn(t, fh.hasRelativeFacingFertilityIssues),
          flowRoute: "FamilyHistoryFlow",
          screenName: "FamilyInfertility",
        });
      }

      if (fh?.hasMotherTakenDESDuringPregnancy != null) {
        items.push({
          label: t("med_history.review.item.des_exposure"),
          value: yn(t, fh.hasMotherTakenDESDuringPregnancy),
          flowRoute: "FamilyHistoryFlow",
          screenName: "Des",
        });
      }

      if (items.length === 0) {
        items.push({
          label: t("med_history.overview.row.family_history"),
          value: t("med_history.review.item.no_family_history_recorded"),
          flowRoute: "FamilyHistoryFlow",
        });
      }

      return items;
    },
  },
};
